import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Switch } from 'react-native-paper';

import { VisibilityLockIcon } from '@/assets';
import { ListItem } from '@/features/list/components/ListItem';
import { useHomeGeoArea } from '@/features/post-editor/hooks/useHomeGeoArea';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isIOS } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  /** Locks the toggle (edit mode). */
  readonly?: boolean;
};

/**
 * "Only visible in home geo area" toggle, bound to the shared form's
 * `hiddenForOtherHomeAreas` field.
 */
export const PostVisibilitySwitch: FC<Props> = ({ readonly }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const { control } = useFormContext<PostFormValues>();

  const { data: homeGeoArea } = useHomeGeoArea();

  return (
    <Controller
      control={control}
      name="hiddenForOtherHomeAreas"
      render={({ field }) => (
        <ListItem
          title={t('onlyIn', {
            name: homeGeoArea?.name ?? t('yourHomeGeoArea')
          })}
          icon={() => (
            <VisibilityLockIcon
              fill={theme.colors.onSurfaceVariant}
              width={24}
              height={24}
            />
          )}
          arrowIcon={() => (
            <Switch
              value={field.value}
              disabled={readonly}
              onValueChange={(value) => {
                if (readonly) return;
                field.onChange(value);
              }}
              ios_backgroundColor={theme.colors.onSurfaceDisabled}
              style={isIOS ? { marginVertical: -4 } : undefined}
            />
          )}
          onPress={readonly ? undefined : () => field.onChange(!field.value)}
        />
      )}
    />
  );
};
